import { jsx } from "panel";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ButtonEl } from "common/components/button";
import { ButtonTheme } from "common/components/button/constants";
import { SvgIconEl } from "common/components/svg-icon";
import { getIconForPropertyType } from "common/report/util/properties";
import { CommonDataTableFields } from "common/components/data-table/types";
import { CheckboxEl } from "common/components/checkbox";
import {
  SortBy,
  SortOrder
} from "common/report/util/sorting/types";
import { PropertyType } from "common/report/constants";
import { TooltipEl } from "common/components/tooltip";
import { FLAT_SORTING_TOOLTIP, GROUPED_SORTING_TOOLTIP, SORTING_TOOLTIP } from "./constants";
export function template() {
  const { sortConfig } = this.params;
  const { segmentColumns, dataColumns } = this.getSegmentAndDataColumns();
  let dropdownContent;
  if (Boolean(segmentColumns) === false || (segmentColumns == null ? void 0 : segmentColumns.length) === 0) {
    return null;
  }
  if (this.params.isLoading) {
    dropdownContent = /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".data-sort-menu-loading"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".mp-skeleton-large"
    }), /* @__PURE__ */ jsx("div", {
      sel: ".mp-skeleton-large"
    }), /* @__PURE__ */ jsx("div", {
      sel: ".mp-skeleton-large"
    }), /* @__PURE__ */ jsx("div", {
      sel: ".mp-skeleton-large"
    })));
  } else {
    let sortEntries;
    if (sortConfig.sortBy === SortBy.Column) {
      sortEntries = segmentColumns == null ? void 0 : segmentColumns.map((column) => {
        const segmentIndex = parseInt(column.field.slice(CommonDataTableFields.SegmentPrefix.length));
        return /* @__PURE__ */ jsx(SegmentSortEntry, {
          segmentIndex: parseInt(column.field.slice(CommonDataTableFields.SegmentPrefix.length)),
          column,
          dataColumns,
          columnSortConfig: this.params.sortConfig.colSortAttrs[segmentIndex],
          handleColumnSortChange: (index, sort) => this.handleColumnSortChange(index, sort)
        });
      });
    } else if (sortConfig.sortBy === SortBy.Value) {
      sortEntries = /* @__PURE__ */ jsx(ValueSortEntry, {
        segmentColumns,
        dataColumns,
        sortConfig: {
          sortOrder: sortConfig.sortOrder,
          sortBy: SortBy.Value,
          valueField: sortConfig.valueField
        },
        handleValueSortChange: (sort) => this.handleValueSortChange(sort)
      });
    }
    const isGroupedSorting = this.params.sortConfig.sortBy === SortBy.Column;
    const groupEl = (segmentColumns == null ? void 0 : segmentColumns.length) > 1 && /* @__PURE__ */ jsx(CheckboxEl, {
      attrs: {
        checked: isGroupedSorting
      },
      on: {
        click: () => this.handleColumnSortingToggle()
      }
    }, "Group segments", /* @__PURE__ */ jsx(TooltipEl, {
      attrs: isGroupedSorting ? GROUPED_SORTING_TOOLTIP : FLAT_SORTING_TOOLTIP
    }));
    dropdownContent = /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".data-sort-menu-entries"
    }, sortEntries), /* @__PURE__ */ jsx("div", {
      sel: ".data-sort-menu-footer"
    }, groupEl, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".data-sort-menu-reset-button",
      attrs: {
        disabled: this.isUsingDefaultSort(),
        elref: `resetButton`,
        theme: ButtonTheme.Tertiary
      },
      on: { click: () => this.handleResetClick() }
    }, "Reset")));
  }
  return /* @__PURE__ */ jsx("div", {
    sel: ".data-sort-menu"
  }, /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      icon: { leftIcon: `sort` },
      label: `Sort`,
      "menu-height": null,
      "menu-width": 500,
      tooltip: SORTING_TOOLTIP
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".data-sort-menu-content",
    attrs: { slot: `body` }
  }, dropdownContent)));
}
function ValueSortEntry({
  dataColumns,
  segmentColumns,
  sortConfig,
  handleValueSortChange
}) {
  const metricSortItems = dataColumns == null ? void 0 : dataColumns.map((column) => {
    var _a;
    return {
      label: (_a = column.sortSelectTitle) != null ? _a : column.title,
      isSelected: sortConfig.valueField === column.field,
      value: {
        sortOrder: sortConfig.sortOrder,
        sortBy: SortBy.Value,
        valueField: column.field
      }
    };
  });
  metricSortItems.push(
    ...segmentColumns.map((column) => ({
      label: column.title,
      isSelected: sortConfig.valueField === column.field,
      value: {
        sortOrder: sortConfig.sortOrder,
        sortBy: SortBy.Label,
        valueField: column.field
      }
    }))
  );
  let sortIconOverride = null;
  const selectedSegmentColumn = segmentColumns.find((column) => column.field === sortConfig.valueField);
  if (selectedSegmentColumn) {
    sortIconOverride = selectedSegmentColumn.contentType === PropertyType.String ? `alpha` : `value`;
  }
  return /* @__PURE__ */ jsx(SortEntry, {
    key: `value-sort`,
    items: metricSortItems,
    sortConfig,
    onChange: (sort) => handleValueSortChange(sort),
    sortIconOverride
  });
}
function SegmentSortEntry({
  segmentIndex,
  column,
  dataColumns,
  columnSortConfig,
  handleColumnSortChange
}) {
  var _a;
  const sortOrder = columnSortConfig.sortOrder;
  const metricSortItems = dataColumns == null ? void 0 : dataColumns.map((sortColumn) => {
    var _a2;
    return {
      label: (_a2 = sortColumn.sortSelectTitle) != null ? _a2 : sortColumn.title,
      isSelected: columnSortConfig.sortBy === SortBy.Value && columnSortConfig.valueField === sortColumn.field,
      value: {
        sortOrder,
        sortBy: SortBy.Value,
        valueField: sortColumn.field
      }
    };
  });
  let labelSortItem = `alphabetical`;
  if (column.contentType === PropertyType.Number) {
    labelSortItem = `range`;
  } else if (column.contentType === PropertyType.Datetime) {
    labelSortItem = `date`;
  }
  const labelSortOption = {
    label: labelSortItem,
    isSelected: columnSortConfig.sortBy === SortBy.Label,
    value: {
      sortOrder,
      sortBy: SortBy.Label
    }
  };
  const sortOptionItems = [...metricSortItems, labelSortOption];
  return /* @__PURE__ */ jsx(SortEntry, {
    key: `segment-${segmentIndex}`,
    items: sortOptionItems,
    sortConfig: columnSortConfig,
    onChange: (sort) => handleColumnSortChange(segmentIndex, sort),
    sortIconOverride: column.contentType === PropertyType.String ? `alpha` : `value`
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: getIconForPropertyType(column.contentType) }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".data-sort-menu-select-label"
  }, (_a = column.sortSelectTitle) != null ? _a : column.title));
}
function SortEntry({
  key,
  items,
  sortConfig,
  onChange,
  sortIconOverride
}, children) {
  const sortOrderIconType = sortConfig.sortBy === SortBy.Label ? sortIconOverride != null ? sortIconOverride : `alpha` : `value`;
  const sortOrderIcon = `sort-${sortOrderIconType}-${sortConfig.sortOrder === SortOrder.Ascending ? `asc` : `desc`}`;
  return /* @__PURE__ */ jsx("div", {
    sel: ".data-sort-menu-entry",
    key
  }, children, /* @__PURE__ */ jsx("div", {
    sel: ".data-sort-menu-select"
  }, /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      "always-show-caret": true,
      theme: SelectTheme.Tertiary
    },
    props: {
      sections: [
        {
          items
        }
      ]
    },
    on: {
      change: (ev) => onChange(ev.detail.item.value)
    }
  }), /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      icon: sortOrderIcon,
      theme: ButtonTheme.Tertiary
    },
    on: {
      click: () => {
        const newSortOrder = sortConfig.sortOrder === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
        onChange({ ...sortConfig, sortOrder: newSortOrder });
      }
    }
  })));
}
